import { useCallback, useEffect, useState } from 'react';
import { getExpertClientNormsHistory } from '@/services/settings.service';
import { DAILY_NORM_KEYS, DailyNormsData, ExpertDailyNormsVersion } from '@/types/settings.types';
import { DayRange } from '@components/clients/calendar/day-balance/useClientDayBalance';

export interface DayNorms {
  norms: DailyNormsData | null;
  /** Того дня діяла версія власних норм експерта. */
  own: boolean;
}

/**
 * Ефективні норми на кожен день видимого проміжку календаря: версія власних норм експерта, що діяла того дня ?? стандартна.
 * Стандартні норми історії не мають - для всіх днів поточні. `refreshKey` - перечитати після зміни норм.
 */
export function useClientNormsByDay(
  clientId: string | number,
  range: DayRange | null,
  standard: DailyNormsData | null,
  refreshKey: unknown,
): (day: string) => DayNorms {
  const [versions, setVersions] = useState<ExpertDailyNormsVersion[]>([]);
  const dateFrom = range?.dateFrom;
  const dateTo = range?.dateTo;

  useEffect(() => {
    if (!dateFrom || !dateTo) {
      setVersions([]);
      return;
    }
    let cancelled = false;
    // Без історії (збій) - календар рахує від стандартних норм.
    getExpertClientNormsHistory(clientId, dateFrom, dateTo)
      .then((data) => { if (!cancelled) setVersions(data); })
      .catch(() => { if (!cancelled) setVersions([]); });
    return () => { cancelled = true; };
  }, [clientId, dateFrom, dateTo, refreshKey]);

  return useCallback(
    (day: string) => {
      const version = versions.find((v) => v.effectiveFrom <= day && (v.effectiveTo == null || day <= v.effectiveTo));
      if (!standard) return { norms: null, own: !!version };
      const norms = { ...standard, ...Object.fromEntries(DAILY_NORM_KEYS.map((key) => [key, version?.[key] ?? standard[key]])) };
      return { norms: norms as DailyNormsData, own: !!version };
    },
    [versions, standard],
  );
}
